import { useEffect, useRef } from 'react';
import { initMtstat, setUserCommonParams } from '@/utils/mtstat';
import {
  setConfig,
  ENV_ENUM,
} from "@meitu/xdesign-widgets/dist/configProvider";
import { initLocalUid } from "@meitu/xdesign-widgets/dist/utils/uidKit";
import { isFromWPS, syncWpsSourceParamsFromUrl, useWpsNavigationIntercept } from "@meitu/xdesign-widgets/dist/wps";
import { getAppKey, MODULE_ID, getClientID } from "@/constants";
import { useStoreActions, useStoreState } from "@/store";
import { initEnterprise } from "@/services/enterprise";
import { isMobile } from "@/utils";
import {
  initABTest,
  isOpenDeepSeek,
  isOpenPptCommercialization,
} from '@/utils/abtest';
import { checkIsInSdk } from '@/utils/sdk';
import {
  markRecentFeature,
  RecentFeatureKey,
} from '@meitu/xdesign-widgets/dist/recentFeatures';
import { openVipModal } from '@/utils/login';
import { triggerGenerateStorageAlert } from '@/services/storage-cost';

const AppLogic = () => {
  const finishWidgetsConfig = useStoreActions(
    (action) => action.finishWidgetsConfig,
  );
  const setIsOpenDeepSeek = useStoreActions(
    (actions) => actions.setIsOpenDeepSeek,
  );
  const setIsOpenPptCommercialization = useStoreActions(
    (actions) => actions.setIsOpenPptCommercialization,
  );

  const finishTrack = useStoreState((state) => state.finishTrack);
  const userInfo = useStoreState((state) => state.userModel.userInfo);
  const finishFetchUserInfo = useStoreState(
    (state) => state.userModel.isRequested,
  );
  const storageCostInitUserIdRef = useRef<number | null>(null);
  const disposeMtstatRef = useRef<(() => void) | undefined>();

  const handleInitAbTest = async () => {
    await initABTest();
    if (isMobile()) {
      setIsOpenDeepSeek(true);
      return;
    }
    if (checkIsInSdk()) {
      setIsOpenDeepSeek(false);
    } else {
      isOpenDeepSeek().then((isOpen) => {
        setIsOpenDeepSeek(isOpen);
      });
    }
    isOpenPptCommercialization().then((isOpen) => {
      setIsOpenPptCommercialization(isOpen);
    });
  };

  useWpsNavigationIntercept();
  syncWpsSourceParamsFromUrl();

  useEffect(() => {
    // xdesign-widgets组件公共参数
    setConfig("client_id", getClientID());
    setConfig("env", process.env.NEXT_PUBLIC_ENV as ENV_ENUM);
    setConfig("app_key", getAppKey());
    setConfig("module_id", MODULE_ID);
    setConfig("theme", "light");
    setConfig("isTeam", true);
    setConfig("isWpsUser", isFromWPS());
    finishWidgetsConfig();

    // 依赖上面env的设置
    initLocalUid();

    const initFn = async () => {
      // 定制企业版逻辑
      await initEnterprise();
      const is = await import('@meitu/is');
      const runInitMtstat = () => {
        disposeMtstatRef.current?.();
        disposeMtstatRef.current = initMtstat(handleInitAbTest);
      };
      if (is.isMobile && (is.isXiuXiu || is.isPoster) && is.isIOS) {
        const onWebviewJsBridgeReady = () => {
          console.log('【WebviewJsBridgeReady】');
          // 初始化埋点SDK逻辑
          runInitMtstat();
        };
        window.addEventListener('WebviewJsBridgeReady', onWebviewJsBridgeReady);
        return () => {
          window.removeEventListener('WebviewJsBridgeReady', onWebviewJsBridgeReady);
        };
      } else {
        // 初始化埋点SDK逻辑
        runInitMtstat();
      }
    };

    let isDisposed = false;
    let cleanupMtstatInit: (() => void) | undefined;
    initFn().then((cleanup) => {
      if (isDisposed) {
        cleanup?.();
        return;
      }
      cleanupMtstatInit = cleanup;
    });
    markRecentFeature(RecentFeatureKey.LivePPT);
    return () => {
      isDisposed = true;
      cleanupMtstatInit?.();
      disposeMtstatRef.current?.();
      disposeMtstatRef.current = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //埋点初始化完成并且用户信息请求完成
  useEffect(() => {
    console.log(
      'finishTrack===',
      finishTrack,
      finishFetchUserInfo,
      window.mtstat,
    );
    if (finishTrack && finishFetchUserInfo) {
      setUserCommonParams({
        isVip: userInfo?.is_vip,
        availableAmount: userInfo?.available_amount,
      });
    }
  }, [
    finishTrack,
    finishFetchUserInfo,
    userInfo?.available_amount,
    userInfo?.is_vip,
  ]);

  useEffect(() => {
    if (userInfo) {
      setConfig('userInfo', userInfo);
    }
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo?.id) {
      return;
    }
    if (storageCostInitUserIdRef.current === userInfo.id) {
      return;
    }

    storageCostInitUserIdRef.current = userInfo.id;
    triggerGenerateStorageAlert({
      userInfo,
      onOpenVipModal: () => {
        openVipModal({
          entrance: 'storage_space',
          subscribe_type: '',
        });
      },
    });
  }, [userInfo]);

  return null;
};

export default AppLogic;
